import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShift } from '../context/ShiftContext';
import api from '../config/api';

interface Store {
  id: number;
  name: string;
  address: string;
  city?: string;
  state?: string;
}

interface RecentAlert {
  id: number;
  store_name: string;
  alert_type: string;
  severity: string;
  title: string;
  status: string;
  created_at: string;
}

const SEVERITY_BADGE: Record<string, string> = {
  low: 'badge badge-neutral',
  medium: 'badge badge-info',
  high: 'badge badge-warning',
  critical: 'badge badge-critical',
};

const STATUS_BADGE: Record<string, string> = {
  open: 'badge badge-info',
  acknowledged: 'badge badge-warning',
  resolved: 'badge badge-success',
  closed: 'badge badge-neutral',
};

export default function DashboardPage() {
  const { activeShift, checkInToStore } = useShift();
  const navigate = useNavigate();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkInStatus, setCheckInStatus] = useState<Record<number, 'idle' | 'loading' | 'done'>>({});
  const [toast, setToast] = useState('');
  const [recentAlerts, setRecentAlerts] = useState<RecentAlert[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function fetchStores() {
      try {
        const res = await api.get('/monitors/stores');
        if (!cancelled) {
          setStores(res.data.stores);
          setLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          const msg = err.message || 'Failed to load stores.';
          setError(msg);
          setLoading(false);
        }
      }
    }
    fetchStores();
    return () => { cancelled = true; };
  }, []);

  // Fetch recent alerts
  useEffect(() => {
    api.get('/monitors/alerts?limit=5').then(res => {
      setRecentAlerts(res.data.alerts || []);
    }).catch(() => {});
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(''), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleCheckIn = async (e: React.MouseEvent, store: Store) => {
    e.stopPropagation();
    setCheckInStatus((prev) => ({ ...prev, [store.id]: 'loading' }));
    try {
      await checkInToStore(store.id);
      setCheckInStatus((prev) => ({ ...prev, [store.id]: 'done' }));
      setToast(`Checked in to ${store.name}`);
      setTimeout(() => {
        setCheckInStatus((prev) => ({ ...prev, [store.id]: 'idle' }));
      }, 2000);
    } catch {
      setCheckInStatus((prev) => ({ ...prev, [store.id]: 'idle' }));
      setToast('Check-in failed');
    }
  };

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <h1 style={styles.pageTitle}>Dashboard</h1>
        <h2 style={{ ...styles.sectionTitle, marginBottom: 20 }}>Your Assigned Stores</h2>

        {loading && <p style={styles.statusText}>Loading stores...</p>}

        {error && (
          <div style={styles.errorBanner}>
            {error}
          </div>
        )}

        {!loading && !error && stores.length === 0 && (
          <p style={styles.statusText}>No stores assigned to you yet.</p>
        )}

        {!loading && stores.length > 0 && (
          <div style={styles.grid}>
            {stores.map((store, index) => {
              const status = checkInStatus[store.id] || 'idle';
              return (
                <div
                  key={store.id}
                  style={{
                    ...styles.card,
                    animation: 'fadeIn 0.2s ease-out both',
                    animationDelay: `${index * 40}ms`,
                  }}
                  onClick={() => navigate(`/stores/${store.id}`, { state: { storeName: store.name } })}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = 'var(--shadow-md)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}
                >
                  <div style={styles.cardHeader}>
                    <h3 style={styles.cardName}>{store.name}</h3>
                    {activeShift && (
                      <button
                        onClick={(e) => handleCheckIn(e, store)}
                        disabled={status !== 'idle'}
                        style={{
                          ...styles.checkInBtn,
                          ...(status === 'done' ? styles.checkInDone : {}),
                        }}
                      >
                        {status === 'loading' ? '...' : status === 'done' ? 'Checked In' : 'Check In'}
                      </button>
                    )}
                  </div>
                  <p style={styles.cardAddress}>
                    {store.address}
                    {store.city && `, ${store.city}`}
                    {store.state && `, ${store.state}`}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* Recent Alerts */}
        {recentAlerts.length > 0 && (
          <div style={styles.alertsSection}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Recent Alerts</h2>
              <button onClick={() => navigate('/alerts')} style={styles.navLink}>View All &rarr;</button>
            </div>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Severity</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Title</th>
                    <th style={styles.th}>Store</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAlerts.map(a => (
                    <tr
                      key={a.id}
                      style={styles.tr}
                      onClick={() => navigate(`/alerts/${a.id}`)}
                      onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--table-row-hover)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
                    >
                      <td style={styles.td}>
                        <span className={SEVERITY_BADGE[a.severity] || 'badge badge-neutral'}>{a.severity}</span>
                      </td>
                      <td style={styles.td}>
                        <span className={STATUS_BADGE[a.status] || 'badge badge-info'}>{a.status}</span>
                      </td>
                      <td style={{ ...styles.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                        {a.title}
                      </td>
                      <td style={styles.td}>{a.store_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Toast */}
      {toast && (
        <div style={styles.toast}>{toast}</div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {},
  main: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '32px 24px',
  },
  pageTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-2xl)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: '0 0 var(--space-8)',
  },
  sectionTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-lg)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  navLink: {
    padding: '6px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'transparent',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--accent-text)',
    cursor: 'pointer',
  },
  statusText: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '14px 18px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
    marginBottom: 16,
  },
  card: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
    borderLeft: '3px solid var(--accent)',
    padding: '20px 22px',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  cardName: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: '0 0 6px',
  },
  cardAddress: {
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    margin: 0,
    lineHeight: 1.5,
  },
  checkInBtn: {
    padding: '4px 10px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--status-success)',
    background: 'transparent',
    color: 'var(--status-success)',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },
  checkInDone: {
    background: 'var(--status-success-muted)',
    borderColor: 'var(--status-success)',
    color: 'var(--status-success)',
    cursor: 'default',
  },
  alertsSection: {
    marginTop: 32,
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 'var(--text-sm)',
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 14px',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    background: 'var(--table-header-bg)',
    borderBottom: '1px solid var(--border-subtle)',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    borderBottom: '1px solid var(--border-subtle)',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  td: {
    padding: '12px 14px',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
    verticalAlign: 'middle' as const,
    whiteSpace: 'nowrap' as const,
  },
  toast: {
    position: 'fixed' as const,
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'var(--bg-surface-overlay)',
    color: 'var(--text-primary)',
    padding: '10px 24px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 2000,
    border: '1px solid var(--border-subtle)',
  },
};
